import {
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import Peer, { DataConnection } from "peerjs";
import {
  ACCENT_OPTIONS,
  AVATAR_OPTIONS,
  MAX_PLAYERS,
  canStartGame,
  calculateScores,
  cloneState,
  createLobbyState,
  createPlayer,
  drawFromSource,
  jackSwapWithPlayer,
  knock,
  matchAndDiscard,
  addPlayer,
  buildClientState,
  GolfClientState,
  InternalGolfState,
  PeekedCard,
  discardDrawnCard,
  replaceDrawnCard,
  revealOpponentCard,
  revealOwnWithNine,
  removePlayer,
  resetGame,
  startGame,
} from "./golfEngine";
import { getGolfAudio } from "./golfAudio";
import "./GolfPage.css";

type BoardMode = "idle" | "host" | "guest";

type JoinRequest = {
  type: "join-request";
  name: string;
  avatar: string;
  accent: string;
};

type StartRequest = { type: "start-game" };
type DrawRequest = { type: "draw"; source: "deck" | "discard" };
type ReplaceRequest = { type: "replace"; index: number };
type DiscardRequest = { type: "discard" };
type RevealOwnRequest = { type: "reveal-own"; index: number };
type MatchRequest = { type: "match-discard"; index: number };
type PeekRequest = { type: "peek-opponent"; targetPlayerId: string; index: number };
type JackRequest = {
  type: "jack-swap";
  targetPlayerId: string;
  myIndex: number;
  theirIndex: number;
};
type KnockRequest = { type: "knock" };
type ResetRequest = { type: "reset-game" };

type HostActionMessage =
  | StartRequest
  | DrawRequest
  | ReplaceRequest
  | DiscardRequest
  | RevealOwnRequest
  | MatchRequest
  | PeekRequest
  | JackRequest
  | KnockRequest
  | ResetRequest;

type HostIncomingMessage = JoinRequest | HostActionMessage;

type StateMessage = { type: "state"; state: GolfClientState };
type JoinedMessage = { type: "joined"; playerId: string; roomCode: string };
type ErrorMessage = { type: "error"; message: string };
type PeekMessage = { type: "peek"; card: PeekedCard };
type HostOutgoingMessage = StateMessage | JoinedMessage | ErrorMessage | PeekMessage;

interface PlayerProfile {
  name: string;
  avatar: string;
  accent: string;
}

function sanitizeRoomCode(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
}

function createPeerId(): string {
  return `golf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function safeParseMessage<T>(data: unknown): T | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  return data as T;
}

function defaultProfile(): PlayerProfile {
  return {
    name: "Golf Player",
    avatar: AVATAR_OPTIONS[0],
    accent: ACCENT_OPTIONS[0],
  };
}

export default function GolfPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const initialRoomCode = useMemo(
    () => sanitizeRoomCode(searchParams.get("room") || ""),
    [searchParams],
  );

  const [mode, setMode] = useState<BoardMode>("idle");
  const [roomCodeInput, setRoomCodeInput] = useState(initialRoomCode);
  const [roomCode, setRoomCode] = useState("");
  const [internalState, setInternalState] = useState<InternalGolfState | null>(null);
  const [clientState, setClientState] = useState<GolfClientState | null>(null);
  const [myPlayerId, setMyPlayerId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [profile, setProfile] = useState<PlayerProfile>(() => {
    try {
      const raw = localStorage.getItem("golf-player-profile");
      if (raw) {
        const parsed = JSON.parse(raw) as PlayerProfile;
        if (parsed?.name && parsed?.avatar && parsed?.accent) {
          return parsed;
        }
      }
    } catch {
      // keep defaults when stale
    }
    return defaultProfile();
  });
  const [peekedCards, setPeekedCards] = useState<PeekedCard[]>([]);

  const [selectedMatchIndex, setSelectedMatchIndex] = useState<number | null>(null);
  const [isUsingEight, setIsUsingEight] = useState(false);
  const [peekTargetPlayerId, setPeekTargetPlayerId] = useState("");
  const [peekTargetIndex, setPeekTargetIndex] = useState(0);

  const [isUsingNine, setIsUsingNine] = useState(false);
  const [revealIndex, setRevealIndex] = useState(0);

  const [isUsingJack, setIsUsingJack] = useState(false);
  const [jackTargetPlayerId, setJackTargetPlayerId] = useState("");
  const [jackMyIndex, setJackMyIndex] = useState(0);
  const [jackTheirIndex, setJackTheirIndex] = useState(0);

  const peerRef = useRef<Peer | null>(null);
  const connectionRef = useRef<DataConnection | null>(null);
  const connectionsRef = useRef<Map<string, DataConnection>>(new Map());
  const connectionPlayerRef = useRef<Map<string, string>>(new Map());
  const isHostRef = useRef(false);
  const hostPlayerIdRef = useRef("");
  const internalStateRef = useRef<InternalGolfState | null>(null);
  const hostStatusRef = useRef<null | "lobby" | "playing" | "finished">(null);
  const autoJoinTriedRef = useRef(false);
  const sounds = useMemo(() => getGolfAudio(), []);

  const safePlay = (fn: () => void): void => {
    try {
      fn();
    } catch {
      // defer when autoplay isn't allowed
    }
  };

  useEffect(() => {
    localStorage.setItem("golf-player-profile", JSON.stringify(profile));
  }, [profile]);

  useEffect(() => {
    internalStateRef.current = internalState;
  }, [internalState]);

  const isHost = mode === "host";
  const viewState = clientState;

  const shareLink = useMemo(() => {
    if (!viewState?.roomCode) {
      return "";
    }
    return `${window.location.origin}/golf?room=${viewState.roomCode}`;
  }, [viewState?.roomCode]);

  const hostPlayer = useMemo(() => {
    if (!viewState) {
      return null;
    }
    return viewState.players.find((entry) => entry.isHost) ?? null;
  }, [viewState]);

  const currentPlayer = useMemo(() => {
    if (!viewState) {
      return null;
    }
    return viewState.players.find((entry) => entry.id === myPlayerId) ?? null;
  }, [viewState, myPlayerId]);

  const myTurn = currentPlayer ? viewState?.turnPlayerId === currentPlayer.id : false;
  const drawnCard = currentPlayer && viewState && viewState.pendingPlayerId === currentPlayer.id
    ? (viewState.pendingPlayerId === currentPlayer.id ? viewState.drawnCardForMe : null)
    : null;

  const opponentPlayers = viewState?.players.filter((entry) => entry.id !== myPlayerId) ?? [];
  const canMatchIndices = useMemo(() => {
    if (!currentPlayer || !drawnCard) {
      return [];
    }
    return currentPlayer.cards
      .map((card, index) => (card.visible && card.rank === drawnCard.rank ? index : -1))
      .filter((index) => index !== -1);
  }, [currentPlayer, drawnCard]);

  const canUseEight = Boolean(
    currentPlayer &&
      viewState?.pendingPlayerId === currentPlayer.id &&
      drawnCard &&
      drawnCard.rank === 8,
  );
  const canUseNine = Boolean(
    currentPlayer &&
      viewState?.pendingPlayerId === currentPlayer.id &&
      drawnCard &&
      drawnCard.rank === 9,
  );
  const canUseJack = Boolean(
    currentPlayer &&
      viewState?.pendingPlayerId === currentPlayer.id &&
      drawnCard &&
      drawnCard.rank === 11,
  );

  const hiddenCardChoices = useMemo(() => {
    if (!currentPlayer) {
      return [];
    }
    return currentPlayer.cards
      .map((card, index) => ({ card, index }))
      .filter((entry) => !entry.card.visible)
      .map((entry) => entry.index);
  }, [currentPlayer]);

  const finalScores = useMemo(() => {
    if (!viewState || viewState.status !== "finished") {
      return [];
    }
    if (internalState?.status === "finished") {
      return calculateScores(internalState.players);
    }
    return viewState.players.map((person) => ({
      playerId: person.id,
      score: person.score ?? 0,
    }));
  }, [internalState, viewState]);

  useEffect(() => {
    if (autoJoinTriedRef.current) {
      return;
    }
    if (!initialRoomCode) {
      return;
    }
    autoJoinTriedRef.current = true;
    if (mode === "idle") {
      void joinRoom(initialRoomCode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRoomCode, mode]);

  useEffect(() => {
    const mountedConnections = connectionsRef.current;
    const mountedPeer = peerRef.current;
    const mountedConnection = connectionRef.current;

    return () => {
      mountedPeer?.destroy();
      mountedConnections.forEach((connection) => connection.close());
      mountedConnection?.close();
    };
  }, []);

  useEffect(() => {
    if (!internalState || !isHostRef.current) {
      return;
    }

    const hostViewId = hostPlayerIdRef.current;
    if (hostViewId) {
      setClientState(buildClientState(internalState, hostViewId));
    }

    if (internalState.status === "finished" && hostStatusRef.current !== "finished") {
      safePlay(() => sounds.playChampagnePop());
    }

    connectionsRef.current.forEach((connection, key) => {
      const playerId = connectionPlayerRef.current.get(key);
      if (!playerId || !connection.open) {
        return;
      }
      const payload = buildClientState(internalState, playerId);
      connection.send({ type: "state", state: payload } as StateMessage);
    });

    hostStatusRef.current = internalState.status;
  }, [internalState, sounds]);

  const clearMatchInputs = (): void => {
    setSelectedMatchIndex(null);
    setIsUsingEight(false);
    setIsUsingNine(false);
    setIsUsingJack(false);
  };

  const resetForNewRoom = (): void => {
    peerRef.current?.destroy();
    connectionsRef.current.forEach((connection) => connection.close());
    connectionRef.current?.close();

    peerRef.current = null;
    connectionRef.current = null;
    connectionsRef.current.clear();
    connectionPlayerRef.current.clear();

    hostPlayerIdRef.current = "";
    internalStateRef.current = null;
    hostStatusRef.current = null;
    isHostRef.current = false;
    setMode("idle");
    setRoomCode("");
    setInternalState(null);
    setClientState(null);
    setMyPlayerId("");
    setPeekedCards([]);
    setErrorMessage("");
    setCopyStatus("");
    clearMatchInputs();
  };

  const sendHostMessage = (connection: DataConnection, payload: HostOutgoingMessage): void => {
    if (!connection.open) {
      return;
    }
    connection.send(payload);
  };

  const sendHostError = (connection: DataConnection, message: string): void => {
    sendHostMessage(connection, { type: "error", message });
  };

  const syncHostState = (next: InternalGolfState): void => {
    setInternalState(next);
    hostPlayerIdRef.current = next.hostPlayerId || hostPlayerIdRef.current;
    internalStateRef.current = next;
    setClientState(buildClientState(next, hostPlayerIdRef.current));
    setRoomCode(next.roomCode);
  };

  const runHostAction = (playerId: string, message: HostActionMessage): void => {
    const state = internalStateRef.current;
    if (!state) {
      setErrorMessage("Game is not ready.");
      return;
    }

    try {
      const next = cloneState(state);
      const previousStatus = next.status;

      switch (message.type) {
        case "start-game": {
          if (state.hostPlayerId !== playerId) {
            throw new Error("Only the host can start the game.");
          }
          const started = startGame(next);
          setErrorMessage("");
          syncHostState(started);
          safePlay(() => sounds.playGolfClap());
          break;
        }
        case "draw": {
          const updated = drawFromSource(next, playerId, message.source);
          syncHostState(updated);
          safePlay(() => sounds.playCardShuffle());
          break;
        }
        case "replace": {
          const updated = replaceDrawnCard(next, playerId, message.index);
          syncHostState(updated);
          break;
        }
        case "discard": {
          const updated = discardDrawnCard(next, playerId);
          syncHostState(updated);
          break;
        }
        case "reveal-own": {
          const updated = revealOwnWithNine(next, playerId, message.index);
          syncHostState(updated);
          break;
        }
        case "match-discard": {
          const updated = matchAndDiscard(next, playerId, message.index);
          syncHostState(updated);
          break;
        }
        case "peek-opponent": {
          const peek = revealOpponentCard(
            next,
            playerId,
            message.targetPlayerId,
            message.index,
          );
          let targetConnection: DataConnection | undefined;
          connectionsRef.current.forEach((connection, peerId) => {
            if (connectionPlayerRef.current.get(peerId) === playerId) {
              targetConnection = connection;
            }
          });

          if (targetConnection) {
            sendHostMessage(targetConnection, { type: "peek", card: peek });
          }
          setPeekedCards((cards) => [...cards, peek]);
          break;
        }
        case "jack-swap": {
          const updated = jackSwapWithPlayer(
            next,
            playerId,
            message.targetPlayerId,
            message.myIndex,
            message.theirIndex,
          );
          syncHostState(updated);
          safePlay(() => sounds.playCardShuffle());
          break;
        }
        case "knock": {
          const updated = knock(next, playerId);
          syncHostState(updated);
          safePlay(() => sounds.playGolfClap());
          break;
        }
        case "reset-game": {
          const updated = resetGame(next);
          syncHostState(updated);
          break;
        }
        default:
          throw new Error("Invalid action.");
      }

      if (internalStateRef.current?.status === "finished" && previousStatus !== "finished") {
        safePlay(() => sounds.playChampagnePop());
      }

      clearMatchInputs();
    } catch (error) {
      const messageText = error instanceof Error ? error.message : "Unable to apply action.";
      setErrorMessage(messageText);
    }
  };

  const handleHostJoin = (connection: DataConnection, message: JoinRequest): void => {
    const state = internalStateRef.current;
    if (!state) {
      sendHostError(connection, "Host state unavailable.");
      return;
    }
    if (state.status !== "lobby") {
      sendHostError(connection, "Game already started.");
      return;
    }
    try {
      const name = message.name.trim() || "Guest";
      const next = addPlayer(
        cloneState(state),
        name,
        message.avatar,
        message.accent,
        connection.peer,
        false,
      );

      connectionPlayerRef.current.set(connection.peer, next.playerId);
      syncHostState(next.state);
      sendHostMessage(connection, {
        type: "joined",
        playerId: next.playerId,
        roomCode: next.state.roomCode,
      });
      sendHostMessage(connection, { type: "state", state: buildClientState(next.state, next.playerId) });
    } catch (error) {
      sendHostError(
        connection,
        error instanceof Error ? error.message : "Could not join this room.",
      );
    }
  };

  const handleHostData = (connection: DataConnection, raw: unknown): void => {
    const message = safeParseMessage<HostIncomingMessage>(raw);
    if (!message) {
      sendHostError(connection, "Could not parse message.");
      return;
    }

    if (message.type === "join-request") {
      handleHostJoin(connection, message);
      return;
    }

    const state = internalStateRef.current;
    if (!state) {
      sendHostError(connection, "Room is not ready.");
      return;
    }

    const playerId = connectionPlayerRef.current.get(connection.peer);
    if (!playerId) {
      sendHostError(connection, "Join the room before taking actions.");
      return;
    }
    runHostAction(playerId, message);
  };

  const setupHostConnection = (connection: DataConnection): void => {
    connectionsRef.current.set(connection.peer, connection);

    connection.on("data", (raw) => {
      handleHostData(connection, raw);
    });

    connection.on("close", () => {
      const playerId = connectionPlayerRef.current.get(connection.peer);
      connectionPlayerRef.current.delete(connection.peer);
      connectionsRef.current.delete(connection.peer);
      if (!playerId) {
        return;
      }

      const state = internalStateRef.current;
      if (!state) {
        return;
      }
      try {
        const next = removePlayer(state, playerId);
        syncHostState(next);
      } catch {
        // ignore disconnect state sync errors
      }
    });

    connection.on("error", () => {
      // disconnect paths are handled in close
    });
  };

  const setupHost = async (): Promise<void> => {
    resetForNewRoom();
    const code = "BEAR";
    if (code.length < 4) {
      setErrorMessage("Room code must be at least 4 characters.");
      setMode("idle");
      return;
    }

    const peerId = `golf-${code}`;
    const peer = new Peer(peerId);
    peerRef.current = peer;
    isHostRef.current = true;
    setMode("host");
    setErrorMessage("");
    setCopyStatus("");
    setRoomCode("");

    peer.on("open", () => {
      const lobby = createLobbyState(code, peerId);
      const player = createPlayer(
        profile.name,
        profile.avatar,
        profile.accent,
        peerId,
        true,
      );
      const seeded = {
        ...lobby,
        hostPlayerId: player.id,
        players: [player],
        lastMessage: "Room created. Share your room code with friends.",
      };

      hostPlayerIdRef.current = player.id;
      setMyPlayerId(player.id);
      setRoomCode(code);
      setRoomCodeInput(code);
      syncHostState(seeded);
    });

    peer.on("connection", (connection) => {
      if (!isHostRef.current) {
        return;
      }
      setupHostConnection(connection);
    });

    peer.on("error", (error) => {
      setErrorMessage(error.message);
      setMode("idle");
      isHostRef.current = false;
    });
  };

  const joinRoom = async (rawRoomCode: string): Promise<void> => {
    const targetCode = sanitizeRoomCode(rawRoomCode);
    if (!targetCode) {
      setErrorMessage("Paste a valid room code.");
      return;
    }
    resetForNewRoom();
    const peer = new Peer(createPeerId());
    peerRef.current = peer;
    isHostRef.current = false;
    setMode("guest");
    setErrorMessage("");
    setRoomCode(targetCode);

    peer.on("open", () => {
      const hostPeerId = `golf-${targetCode}`;
      const connection = peer.connect(hostPeerId);
      connectionRef.current = connection;

      connection.on("open", () => {
        connection.send({
          type: "join-request",
          name: profile.name,
          avatar: profile.avatar,
          accent: profile.accent,
        } as JoinRequest);
      });

      connection.on("data", (raw) => {
        const message = safeParseMessage<HostOutgoingMessage>(raw);
        if (!message) {
          return;
        }

        if (message.type === "state") {
          setClientState(message.state);
          setRoomCode(message.state.roomCode);
          return;
        }

        if (message.type === "joined") {
          setMyPlayerId(message.playerId);
          return;
        }

        if (message.type === "peek") {
          setPeekedCards((cards) => [...cards, message.card]);
          return;
        }

        if (message.type === "error") {
          setErrorMessage(message.message);
          return;
        }
      });

      connection.on("close", () => {
        setErrorMessage("Disconnected from room.");
      });
      connection.on("error", () => {
        setErrorMessage("Could not connect to this room. Check the code and try again.");
      });
    });

    peer.on("error", (error) => {
      setErrorMessage(error.message);
      setMode("idle");
      isHostRef.current = false;
    });
  };

  const sendAction = (action: HostActionMessage): void => {
    if (isHostRef.current) {
      if (!myPlayerId) {
        setErrorMessage("Host identity missing.");
        return;
      }
      runHostAction(myPlayerId, action);
      return;
    }

    const connection = connectionRef.current;
    if (!connection || !connection.open) {
      setErrorMessage("No live connection.");
      return;
    }
    connection.send(action);
  };

  const onCreateSubmit = (event: FormEvent): void => {
    event.preventDefault();
    void setupHost();
  };

  const onJoinSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const code = sanitizeRoomCode(roomCodeInput);
    if (!code) {
      setErrorMessage("Enter a room code.");
      return;
    }
    void joinRoom(code);
  };

  const doCopy = async (): Promise<void> => {
    if (!shareLink) {
      return;
    }
    await navigator.clipboard.writeText(shareLink);
    setCopyStatus("Copied");
    setTimeout(() => {
      setCopyStatus("");
    }, 1200);
  };

  const doDrawDeck = (): void => {
    sendAction({ type: "draw", source: "deck" });
  };

  const doDrawDiscard = (): void => {
    if (!viewState || viewState.discardCount === 0) {
      return;
    }
    sendAction({ type: "draw", source: "discard" });
  };

  const doReplace = (index: number): void => {
    sendAction({ type: "replace", index });
  };

  const doDiscard = (): void => {
    sendAction({ type: "discard" });
  };

  const doMatchAndDiscard = (): void => {
    if (selectedMatchIndex === null) {
      return;
    }
    sendAction({ type: "match-discard", index: selectedMatchIndex });
    setSelectedMatchIndex(null);
  };

  const doRevealOwn = (): void => {
    if (!hiddenCardChoices.length) {
      return;
    }
    sendAction({ type: "reveal-own", index: revealIndex });
    setIsUsingNine(false);
  };

  const doPeekOpponent = (): void => {
    if (!peekTargetPlayerId) {
      return;
    }
    sendAction({
      type: "peek-opponent",
      targetPlayerId: peekTargetPlayerId,
      index: peekTargetIndex,
    });
    setIsUsingEight(false);
  };

  const doJackSwap = (): void => {
    if (!jackTargetPlayerId) {
      return;
    }
    sendAction({
      type: "jack-swap",
      targetPlayerId: jackTargetPlayerId,
      myIndex: jackMyIndex,
      theirIndex: jackTheirIndex,
    });
    setIsUsingJack(false);
  };

  const doStartGame = (): void => {
    if (!internalState || !canStartGame(internalState)) {
      return;
    }
    sendAction({ type: "start-game" });
  };

  const doKnock = (): void => {
    sendAction({ type: "knock" });
  };

  const doReset = (): void => {
    if (!isHost) {
      return;
    }
    sendAction({ type: "reset-game" });
  };

  const renderCardFace = (
    rank: string,
    suit: string,
    hidden = false,
  ): JSX.Element => {
    if (hidden) {
      return <div className="golf-card-blank">?</div>;
    }

    const isRed = suit === "♥" || suit === "♦";

    return (
      <div className={`golf-card-front-content ${isRed ? "is-red" : ""}`}>
        <span className="golf-card-corner golf-card-corner-top">
          {rank}
          <small>{suit}</small>
        </span>
        <span className="golf-card-center">{suit}</span>
        <span className="golf-card-corner golf-card-corner-bottom">
          {rank}
          <small>{suit}</small>
        </span>
      </div>
    );
  };

  return (
    <div className="golf-shell">
      <div className="golf-page">
        <header className="golf-header">
          <h1>Country Club Golf</h1>
          <p className="golf-subtitle">2–4 players, lowest score wins.</p>
          {viewState && (
            <p className="golf-room-code">
              Room <strong>{viewState.roomCode}</strong>
            </p>
          )}
        </header>

        {viewState ? (
          <section className="golf-topbar">
            <p className="golf-last-message">{viewState.lastMessage}</p>
            <div className="golf-topbar-actions">
              <button className="golf-button" onClick={doCopy} type="button">
                {copyStatus || "Copy Invite Link"}
              </button>
            </div>
          </section>
        ) : null}

        {errorMessage && <p className="golf-error">{errorMessage}</p>}

        {mode === "idle" && (
          <form className="golf-setup">
            <section className="golf-card-editor">
              <label htmlFor="golf-name">Player Name</label>
              <input
                id="golf-name"
                value={profile.name}
                onChange={(event) =>
                  setProfile((prev) => ({ ...prev, name: event.target.value.slice(0, 20) }))
                }
              />

              <label>Avatar</label>
              <div className="golf-avatar-list">
                {AVATAR_OPTIONS.map((avatar) => (
                  <button
                    key={avatar}
                    type="button"
                    className={`golf-avatar ${profile.avatar === avatar ? "active" : ""}`}
                    onClick={() => setProfile((prev) => ({ ...prev, avatar }))}
                  >
                    {avatar}
                  </button>
                ))}
              </div>

              <label>Accent</label>
              <div className="golf-accent-list">
                {ACCENT_OPTIONS.map((accent) => (
                  <button
                    key={accent}
                    type="button"
                    className={`golf-accent ${profile.accent === accent ? "active" : ""}`}
                    onClick={() => setProfile((prev) => ({ ...prev, accent }))}
                    style={{ backgroundColor: accent }}
                  />
                ))}
              </div>
            </section>

            <section className="golf-room-entry">
              <label htmlFor="golf-code">Room Code</label>
              <input
                id="golf-code"
                value={roomCodeInput}
                onChange={(event) => setRoomCodeInput(sanitizeRoomCode(event.target.value))}
                placeholder="Optional: custom code"
              />
              <div className="golf-room-buttons">
                <button
                  className="golf-button golf-button-secondary"
                  onClick={onCreateSubmit}
                  type="button"
                >
                  Host Room
                </button>
                <button className="golf-button" onClick={onJoinSubmit} type="button">
                  Join Room
                </button>
              </div>
              {initialRoomCode ? (
                <p className="golf-note">Loaded invite code from URL: {initialRoomCode}</p>
              ) : null}
              <p className="golf-note">Need 2 to {MAX_PLAYERS} players.</p>
            </section>
          </form>
        )}

        {viewState && viewState.status === "lobby" && (
          <section className="golf-lobby">
            <h2>Lobby</h2>
            <p>Invite players by sharing this URL.</p>
            <p className="golf-link-preview">{shareLink}</p>
            <ul className="golf-lobby-players">
              {viewState.players.map((person) => (
                <li key={person.id}>
                  <span>{person.avatar}</span>
                  <span>{person.name}</span>
                  {person.id === hostPlayer?.id ? " (Host)" : null}
                </li>
              ))}
            </ul>
            <p>
              Players: {viewState.players.length}/{MAX_PLAYERS}
            </p>
            {isHost ? (
              <button
                className="golf-button"
                disabled={!internalState || !canStartGame(internalState)}
                onClick={doStartGame}
                type="button"
              >
                Start Round
              </button>
            ) : (
              <p>Waiting for the host to start the round.</p>
            )}
          </section>
        )}

        {mode === "guest" && !clientState ? (
          <section className="golf-lobby">
            <p>Joining room {roomCode || initialRoomCode}...</p>
          </section>
        ) : null}

        {viewState && viewState.status !== "lobby" && (
          <section className="golf-scoreboard">
            <h2>Scorecard</h2>
            <div className="golf-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Turn</th>
                    <th>State</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {viewState.players.map((person) => {
                    const final = finalScores.find((entry) => entry.playerId === person.id)?.score;
                    return (
                      <tr key={person.id} className={person.id === viewState.turnPlayerId ? "active" : ""}>
                        <td>
                          <span
                            className="golf-badge"
                            style={{ borderColor: person.accent }}
                          >
                            {person.avatar}
                            {person.name}
                          </span>
                          {person.id === myPlayerId ? " (You)" : ""}
                        </td>
                        <td>{person.id === viewState.turnPlayerId ? "Now" : ""}</td>
                        <td>{person.knocked ? "Knocked" : "In"}</td>
                        <td>{final === undefined ? "-" : final}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {viewState && currentPlayer ? (
          <section className="golf-gameboard">
            <article className="golf-piles">
              <div className="golf-pile">
                <span>Deck</span>
                <strong>{viewState.deckCount}</strong>
              </div>
              <div className="golf-pile">
                <span>Discard</span>
                <strong>{viewState.discardCount}</strong>
                <div className="golf-pile-top">
                  {viewState.discardTop ? `${viewState.discardTop.label}${viewState.discardTop.suit}` : ""}
                </div>
              </div>
            </article>

            <article className="golf-hand">
              <h3>Your Hand</h3>
              <div className="golf-card-grid">
                {currentPlayer.cards.map((card, index) => {
                  const canReplace = myTurn && drawnCard !== null && viewState.pendingPlayerId === myPlayerId;
                  return (
                    <button
                      key={`${currentPlayer.id}-${index}`}
                      type="button"
                      className={`golf-card ${card.visible ? "visible" : "hidden"} ${canReplace ? "can-action" : ""}`}
                      onClick={() => {
                        if (!canReplace) {
                          return;
                        }
                        doReplace(index);
                      }}
                    >
                      <div className={`golf-card-inner ${card.visible ? "is-visible" : ""}`}>
                        <div className="golf-card-back" />
                        <div className="golf-card-front">
                          {renderCardFace(card.label, card.suit, !card.visible)}
                        </div>
                      </div>
                      <small>{index + 1}</small>
                    </button>
                  );
                })}
              </div>
            </article>

            <article className="golf-drawn-zone">
              <div className="golf-drawn-card">
                {drawnCard ? (
                  <div className="golf-card visible is-drawn">
                    <div className="golf-card-inner is-visible">
                      <div className="golf-card-back" />
                      <div className="golf-card-front">
                        {renderCardFace(drawnCard.label, drawnCard.suit)}
                      </div>
                    </div>
                    <p>Drawn Card</p>
                  </div>
                ) : (
                  <p>Draw a card to begin your turn.</p>
                )}
              </div>

              {myTurn ? (
                <div className="golf-controls">
                  {!viewState.pendingPlayerId ? (
                    <>
                      <button className="golf-button" onClick={doDrawDeck} type="button">
                        Draw from Deck
                      </button>
                      <button
                        className="golf-button"
                        onClick={doDrawDiscard}
                        type="button"
                        disabled={viewState.discardCount === 0}
                      >
                        Take from Discard
                      </button>
                      <button className="golf-button golf-button-secondary" onClick={doKnock} type="button">
                        Knock
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="golf-turn-label">Replace one of your cards or discard drawn card.</p>
                      {canMatchIndices.length > 0 ? (
                        <div className="golf-inline-row">
                          <select
                            value={selectedMatchIndex ?? ""}
                            onChange={(event) => {
                              setSelectedMatchIndex(
                                event.target.value === "" ? null : Number(event.target.value),
                              );
                            }}
                          >
                            <option value="">Match and discard</option>
                            {canMatchIndices.map((index) => (
                              <option key={index} value={index}>
                                Card {index + 1}
                              </option>
                            ))}
                          </select>
                          <button
                            className="golf-button golf-button-secondary"
                            onClick={doMatchAndDiscard}
                            disabled={selectedMatchIndex === null}
                            type="button"
                          >
                            Match &amp; Discard
                          </button>
                        </div>
                      ) : null}

                      {canUseEight ? (
                        <div className="golf-inline-row">
                          <button
                            type="button"
                            className="golf-button golf-button-secondary"
                            onClick={() => setIsUsingEight((value) => !value)}
                          >
                            {isUsingEight ? "Cancel 8" : "Use 8: peek opponent card"}
                          </button>
                          {isUsingEight ? (
                            <div className="golf-inline-row">
                              <select
                                value={peekTargetPlayerId}
                                onChange={(event) => setPeekTargetPlayerId(event.target.value)}
                              >
                                <option value="">Select opponent</option>
                                {opponentPlayers.map((person) => (
                                  <option key={person.id} value={person.id}>
                                    {person.name}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={peekTargetIndex}
                                onChange={(event) => setPeekTargetIndex(Number(event.target.value))}
                              >
                                {[0, 1, 2, 3].map((index) => (
                                  <option key={index} value={index}>
                                    Card {index + 1}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="golf-button"
                                onClick={doPeekOpponent}
                                disabled={!peekTargetPlayerId}
                              >
                                Reveal
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {canUseNine ? (
                        <div className="golf-inline-row">
                          <button
                            type="button"
                            className="golf-button golf-button-secondary"
                            onClick={() => setIsUsingNine((value) => !value)}
                          >
                            {isUsingNine ? "Cancel 9" : "Use 9: flip one own card"}
                          </button>
                          {isUsingNine ? (
                            <div className="golf-inline-row">
                              <select
                                value={revealIndex}
                                onChange={(event) => setRevealIndex(Number(event.target.value))}
                              >
                                {hiddenCardChoices.length === 0 ? (
                                  <option value={0}>No hidden cards</option>
                                ) : null}
                                {hiddenCardChoices.map((index) => (
                                  <option key={index} value={index}>
                                    Card {index + 1}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="golf-button"
                                onClick={doRevealOwn}
                                disabled={hiddenCardChoices.length === 0}
                              >
                                Reveal
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {canUseJack ? (
                        <div className="golf-inline-row">
                          <button
                            type="button"
                            className="golf-button golf-button-secondary"
                            onClick={() => setIsUsingJack((value) => !value)}
                          >
                            {isUsingJack ? "Cancel J" : "Use J: swap"}
                          </button>
                          {isUsingJack ? (
                            <div className="golf-inline-row">
                              <select
                                value={jackTargetPlayerId}
                                onChange={(event) => setJackTargetPlayerId(event.target.value)}
                              >
                                <option value="">Select opponent</option>
                                {opponentPlayers.map((person) => (
                                  <option key={person.id} value={person.id}>
                                    {person.name}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={jackMyIndex}
                                onChange={(event) => setJackMyIndex(Number(event.target.value))}
                              >
                                {[0, 1, 2, 3].map((index) => (
                                  <option key={index} value={index}>
                                    My {index + 1}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={jackTheirIndex}
                                onChange={(event) => setJackTheirIndex(Number(event.target.value))}
                              >
                                {[0, 1, 2, 3].map((index) => (
                                  <option key={index} value={index}>
                                    Their {index + 1}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="golf-button"
                                onClick={doJackSwap}
                                disabled={!jackTargetPlayerId}
                              >
                                Swap
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      <button className="golf-button" onClick={doDiscard} type="button">
                        Discard Drawn Card
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <p className="golf-waiting">Waiting for opponent turns...</p>
              )}
            </article>
          </section>
        ) : null}

        {viewState && viewState.status === "finished" ? (
          <section className="golf-final-board">
            <h2>Final Scores</h2>
            <div className="golf-final-grid">
              {viewState.players
                .map((person) => ({
                  person,
                  score: finalScores.find((entry) => entry.playerId === person.id)?.score ?? 0,
                }))
                .sort((first, second) => first.score - second.score)
                .map((entry, rank) => (
                  <div key={entry.person.id} className="golf-final-card">
                    <span>{entry.score}</span>
                    <strong>{entry.person.name}</strong>
                    <small>{entry.person.avatar}</small>
                    <em>{rank === 0 ? "Winner" : `#${rank + 1}`}</em>
                  </div>
                ))}
            </div>
            {isHost ? (
              <button className="golf-button" onClick={doReset} type="button">
                Start New Round
              </button>
            ) : null}
          </section>
        ) : null}

        <section className="golf-peek-log">
          <h4>Special-8 peeks</h4>
          {peekedCards.length === 0 ? (
            <p>No peeks yet.</p>
          ) : (
            <ul>
              {peekedCards
                .slice(-8)
                .map((entry, index) => (
                  <li key={`${entry.playerId}-${entry.index}-${index}`}>
                    {entry.playerId === myPlayerId ? "Your" : "Opponent"} card {entry.index + 1} is {entry.label}
                    {entry.suit}
                  </li>
                ))}
            </ul>
          )}
        </section>

        <section className="golf-footer">
          {mode !== "idle" ? (
            <button className="golf-button golf-button-secondary" onClick={resetForNewRoom} type="button">
              Back to Setup
            </button>
          ) : null}
          <p className="golf-credit">
            Drawn-card secrecy + live multiplayer powered by peer links.
          </p>
        </section>
      </div>
    </div>
  );
}
